import { useState, useCallback, useRef } from 'preact/hooks'
import type { TestResult, Persona } from '../types'
import { getTestList, runTest, runAllTests } from '../services/testRunner'
import { ToolTestPanel } from './ToolTestPanel'
import { CrudTestPanel } from './CrudTestPanel'
import { CostTestPanel } from './CostTestPanel'

type TestTab = 'api' | 'tools' | 'crud' | 'costs'

interface TestPanelProps {
  personas: Persona[]
  selectedPersona: Persona
}

export function TestPanel({ personas, selectedPersona }: TestPanelProps) {
  const [tab, setTab] = useState<TestTab>('api')
  const [results, setResults] = useState<Map<string, TestResult>>(new Map())
  const [running, setRunning] = useState<Set<string>>(new Set())
  const [runningAll, setRunningAll] = useState(false)
  const [log, setLog] = useState<string[]>([])
  const logRef = useRef<HTMLDivElement>(null)

  const tests = getTestList()

  const appendLog = useCallback((line: string) => {
    setLog(prev => [...prev, line])
    requestAnimationFrame(() => {
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
    })
  }, [])

  /** Intercept console.log/error/warn during a test run and pipe to the log panel. */
  const withLogCapture = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    const origLog = console.log
    const origError = console.error
    const origWarn = console.warn

    const capture = (...args: unknown[]) => {
      const line = args.map(a => typeof a === 'string' ? a : JSON.stringify(a, null, 0)?.slice(0, 300) ?? '').join(' ')
      appendLog(line)
    }

    console.log = (...args: unknown[]) => { origLog(...args); capture(...args) }
    console.error = (...args: unknown[]) => { origError(...args); capture(`ERROR: ${args.map(String).join(' ')}`) }
    console.warn = (...args: unknown[]) => { origWarn(...args); capture(`WARN: ${args.map(String).join(' ')}`) }

    try {
      return await fn()
    } finally {
      console.log = origLog
      console.error = origError
      console.warn = origWarn
    }
  }, [appendLog])

  const handleRunTest = useCallback(async (testName: string) => {
    setRunning(prev => new Set(prev).add(testName))
    appendLog(`\n--- Running: ${testName} ---`)
    const result = await withLogCapture(() => runTest(testName))
    setResults(prev => new Map(prev).set(testName, result))
    setRunning(prev => { const n = new Set(prev); n.delete(testName); return n })
    appendLog(`--- ${testName}: ${result.passed ? 'PASSED' : 'FAILED'} (${(result.duration / 1000).toFixed(1)}s) ---`)
  }, [appendLog, withLogCapture])

  const handleRunAll = useCallback(async () => {
    setRunningAll(true)
    setResults(new Map())
    setLog([])
    appendLog(`Starting ${tests.length} API tests`)

    await withLogCapture(async () => {
      const allResults = await runAllTests((name, status) => {
        if (status === 'running') {
          setRunning(prev => new Set(prev).add(name))
        } else {
          setRunning(prev => { const n = new Set(prev); n.delete(name); return n })
        }
      })

      for (const result of allResults) {
        setResults(prev => new Map(prev).set(result.name, result))
      }

      const passed = allResults.filter(r => r.passed).length
      appendLog(`\nAll done: ${passed}/${allResults.length} passed`)
    })

    setRunningAll(false)
  }, [tests, appendLog, withLogCapture])

  const passCount = [...results.values()].filter(r => r.passed).length
  const failCount = [...results.values()].filter(r => !r.passed).length

  return (
    <div class="test-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(250,250,250,0.1)', flexShrink: 0 }}>
        <button onClick={() => setTab('api')} style={tabBtnStyle(tab === 'api')}>API Tests</button>
        <button onClick={() => setTab('tools')} style={tabBtnStyle(tab === 'tools')}>AI Tool Tests</button>
        <button onClick={() => setTab('crud')} style={tabBtnStyle(tab === 'crud')}>Data CRUD Tests</button>
        <button onClick={() => setTab('costs')} style={tabBtnStyle(tab === 'costs')}>Cost Tracking</button>
      </div>

      {tab === 'tools' ? (
        <ToolTestPanel personas={personas} selectedPersona={selectedPersona} />
      ) : tab === 'crud' ? (
        <CrudTestPanel />
      ) : tab === 'costs' ? (
        <CostTestPanel personas={personas} selectedPersona={selectedPersona} />
      ) : (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: test list */}
          <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, margin: 0 }}>API Test Automation</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {results.size > 0 && (
                  <span style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)' }}>
                    {passCount} passed, {failCount} failed
                  </span>
                )}
                <button
                  style={runBtnStyle}
                  onClick={handleRunAll}
                  disabled={runningAll}
                >
                  {runningAll ? 'Running...' : 'Run All'}
                </button>
              </div>
            </div>

            <div class="test-panel__list">
              {tests.map(testName => {
                const result = results.get(testName)
                const isRunning = running.has(testName)

                return (
                  <div key={testName} class="test-panel__item">
                    <div class="test-panel__item-header">
                      <span class="test-panel__status">
                        {isRunning ? '\u23F3' : result ? (result.passed ? '\u2705' : '\u274C') : '\u26AA'}
                      </span>
                      <span class="test-panel__name">{testName}</span>
                      <button
                        class="btn btn-sm"
                        onClick={() => handleRunTest(testName)}
                        disabled={isRunning || runningAll}
                      >
                        Run
                      </button>
                    </div>
                    {result && (
                      <div class={`test-panel__details ${result.passed ? 'test-panel__details--pass' : 'test-panel__details--fail'}`}>
                        <div>{result.details}</div>
                        <div class="test-panel__duration">{(result.duration / 1000).toFixed(1)}s</div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right: console log */}
          <div style={{
            width: 380,
            borderLeft: '1px solid rgba(250,250,250,0.1)',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}>
            <div style={{
              padding: '8px 12px',
              borderBottom: '1px solid rgba(250,250,250,0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(250,250,250,0.5)' }}>Console Log</span>
              <button style={linkBtnStyle} onClick={() => setLog([])}>Clear</button>
            </div>
            <div
              ref={logRef}
              style={{
                flex: 1,
                overflow: 'auto',
                padding: 10,
                fontFamily: 'monospace',
                fontSize: 11,
                lineHeight: '1.5',
                color: 'rgba(250,250,250,0.6)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {log.length === 0
                ? <span style={{ color: 'rgba(250,250,250,0.3)' }}>Run tests to see console output here.{'\n'}Full details also logged to browser console (F12).</span>
                : log.map((line, i) => (
                    <div key={i} style={{
                      color: line.includes('ERROR') || line.includes('FAIL') || line.includes('failed')
                        ? '#ff6b6b'
                        : line.includes('PASS') || line.includes('passed')
                          ? '#66bb6a'
                          : line.includes('WARN')
                            ? '#ffa726'
                            : line.startsWith('---') || line.startsWith('===')
                              ? 'rgba(250,250,250,0.4)'
                              : undefined,
                    }}>
                      {line}
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const tabBtnStyle = (active: boolean): Record<string, string | number> => ({
  padding: '10px 20px',
  border: 'none',
  background: 'transparent',
  color: active ? '#fff' : 'rgba(250,250,250,0.5)',
  borderBottom: active ? '2px solid #4fc3f7' : '2px solid transparent',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: active ? 600 : 400,
})

const runBtnStyle: Record<string, string | number> = {
  padding: '5px 14px',
  border: 'none',
  borderRadius: 6,
  background: 'rgba(45,165,160,0.3)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
}

const linkBtnStyle: Record<string, string | number> = {
  padding: '2px 6px',
  border: 'none',
  background: 'transparent',
  color: 'rgba(79,195,247,0.7)',
  cursor: 'pointer',
  fontSize: 11,
  textDecoration: 'underline',
}
